import type { FunctionComponent } from 'react';
import { useContext, useRef, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type { ProfileVisibilityUser } from '~/shared/data-contract/profile-visibility';
import { useProfileVisibilityUserSearchQuery } from '~/shared/query-hooks/profile-visibility';

import { Autocomplete } from '../../../../design/components/form-fields/AutoComplete';
import { getUserName } from '../../../common/user';

type ProfileVisibilityUserSearchFieldProps = {
    readonly addToList: (user: ProfileVisibilityUser | null) => void;
    readonly appliedUsers: readonly ProfileVisibilityUser[];
};

export const ProfileVisibilityUserSearchField: FunctionComponent<ProfileVisibilityUserSearchFieldProps> = ({
    addToList,
    appliedUsers,
}) => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const userId = applicationState.myUserProfile.get('id').unwrapOr('');
    const [searchTerm, setSearchTerm] = useState('');

    const result = useProfileVisibilityUserSearchQuery(userId, searchTerm);
    const usersInDropdown = result.isSuccess
        ? result.data.filter((user) => !appliedUsers.some((selectedUser) => selectedUser.id === user.id))
        : [];
    const inputRef = useRef<HTMLElement | null>(null);

    return (
        <Autocomplete<ProfileVisibilityUser>
            filterOptions={(options) => options}
            getOptionLabel={(user) => getUserName(Maybe.of(user))}
            id="visibility-configuration-search-input"
            inputRef={inputRef}
            inputValue={searchTerm}
            isBoxed={true}
            label={translate('pages.user-profile.profile-visibility.drawer.sections.search-placeholder')}
            options={usersInDropdown}
            onChange={(user) => {
                addToList(user);
                setSearchTerm('');
                inputRef.current?.focus();
            }}
            onInputChange={(_, value) => setSearchTerm(value)}
        />
    );
};
